import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";
import {translate} from "react-i18nify";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as formulaTypes from "../constants/formulaTypes";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as settings from "../constants/settings";
import * as statsTypes from "../constants/statsTypes";
import * as navigationActions from "../actions/navigationActions";
import * as matchingActions from "../actions/matchingActions";
import * as getterHelper from "../helpers/getterHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as reducerHelper from "../helpers/reducerHelper";

const matchingTabInit = (action$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_INIT),
        switchMap(({payload}) => {
            return [matchingActions.matchingInitEngine(payload.tabId)];
        })
    );

const matchingInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_INIT_ENGINE),
        switchMap(({payload}) => {
            if (
                state$.value.server.requests.some(
                    (r) => r.method === handlers.MATCHING_INIT || r.method === handlers.MATCHING_CAN_BE_USED
                )
            ) {
                return [];
            }

            if (!state$.value.global.matchingInitialized) {
                return [matchingActions.matchingInit(payload.tabId)];
            }

            if (!state$.value.global.matchingAvailable) {
                return [matchingActions.matchingAvailable(payload.tabId)];
            }
            if (
                state$.value.matchingTab[payload.tabId].selectedProduct &&
                state$.value.global.matchingProductList.find(
                    (product) => product.id === state$.value.matchingTab[payload.tabId].selectedProduct.id
                )
            ) {
                return [
                    matchingActions.selectProduct(
                        payload.tabId,
                        state$.value.matchingTab[payload.tabId].selectedProduct
                    )
                ];
            }
            return [];
        })
    );

const matchingTabInitSearch = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_INIT_SEARCH),
        switchMap(({payload}) => {
            if (state$.value.matchingTab[payload.tabId] === undefined) {
                return [];
            }

            return [
                matchingActions.getFandeckList(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].color === null
                        ? null
                        : state$.value.matchingTab[payload.tabId].color.id,
                    null,
                    state$.value.settings.fts
                ),
                matchingActions.getColorList(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].fandeck === null
                        ? null
                        : state$.value.matchingTab[payload.tabId].fandeck.id,
                    null,
                    state$.value.settings.fts
                )
            ];
        })
    );

const matchingTabSelectFandeck = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_FANDECK),
        switchMap(({payload}) => {
            if (state$.value.matchingTab[payload.tabId] === undefined) {
                return [];
            }

            return [
                matchingActions.getColorList(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].fandeck === null
                        ? null
                        : state$.value.matchingTab[payload.tabId].fandeck.id,
                    null,
                    state$.value.settings.fts
                )
            ];
        })
    );

const matchingTabSelectColor = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_COLOR),
        switchMap(({payload}) => {
            if (state$.value.matchingTab[payload.tabId] === undefined) {
                return [];
            }

            return [
                matchingActions.getFandeckList(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].color === null
                        ? null
                        : state$.value.matchingTab[payload.tabId].color.id,
                    null,
                    state$.value.settings.fts
                )
            ];
        })
    );

const setMatchingProduct = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_SAVE_PRODUCT_LIST),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.tabId]?.selectedProduct?.id &&
                state$.value.global.matchingProductList.find(
                    (product) => product.id === state$.value.matchingTab[payload.tabId].selectedProduct.id
                )
            ) {
                return [
                    matchingActions.selectProduct(
                        payload.tabId,
                        state$.value.matchingTab[payload.tabId].selectedProduct
                    )
                ];
            }
            return [];
        })
    );

const matchingTabFromMenu = (action$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_MATCHING_TAB),
        switchMap(({payload}) => {
            return [matchingActions.matchingTabInit(payload.id, payload.product)];
        })
    );

const matchingTabFromPalette = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_FROM_PALETTE, actionTypes.MATCHING_TAB_FROM_COLOR_SEARCH),
        switchMap(({payload}) => {
            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);
            return [
                navigationActions.addNewMatchingTab(newTabId, null, false, payload.product),
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_COLOR_IN_FANDECK_DATA,
                        params: {
                            colorId: payload.colorId,
                            fandeckId: payload.fandeckId,
                            isLocal: payload.local
                        }
                    },
                    method: handlers.MATCHING_TAB_GET_DETAIL,
                    tabId: newTabId
                }
            ];
        })
    );

const matchingTabFromStats = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_FROM_STATS),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            let statisticsId = state$.value.formula[payload.tabId].statisticsId;

            let tab;
            for (tab of state$.value.navigation.tabList) {
                if (
                    tab.type === componentTypes.MATCHING_TAB &&
                    state$.value.matchingTab[tab.id] !== undefined &&
                    state$.value.formula[tab.id] !== undefined &&
                    state$.value.formula[tab.id].statisticsId === statisticsId &&
                    state$.value.formula[tab.id].formula !== null
                ) {
                    return [
                        {
                            type: actionTypes.SET_ACTIVE_TAB,
                            payload: {
                                tabId: tab.id
                            }
                        }
                    ];
                }
            }

            let formula = state$.value.formula[payload.tabId].formula.updateStatsType(statsTypes.AUTOMATIC_CORRECTION);
            formula.colorantList = formula.realColorantList;
            formula.colorantListG = formula.realColorantListG;
            formula.colorantListShot = formula.realColorantListShot;
            formula.setCorrections();
            formula.resetScaling();

            let newTabId = reducerHelper.getNewTabId(state$.value.navigation.tabList);

            let epicRequest = [
                {
                    type: actionTypes.CLONE_FORMULA_DATA,
                    payload: {
                        tabId: payload.tabId,
                        newTabId: newTabId,
                        formula: formula,
                        packageCount: null
                    }
                },
                navigationActions.addNewMatchingTab(newTabId, componentTypes.AUTO_CORRECTION_SCENE, false, null),
                matchingActions.matchingTabInit(newTabId),
                {
                    type: actionTypes.CORRECTION_SAVE_TARGET_FORMULA,
                    payload: {
                        tabId: newTabId,
                        result: state$.value.formula[payload.tabId].spectralData,
                        formula: state$.value.formula[payload.tabId].formula,
                        product: state$.value.formula[payload.tabId].product,
                        fandeck: state$.value.formula[payload.tabId].fandeck,
                        color: state$.value.formula[payload.tabId].color,
                        base: state$.value.formula[payload.tabId].base,
                        package: state$.value.formula[payload.tabId].package
                    }
                }
            ];

            // Quality control measurement
            if (payload.measurementResult !== null) {
                epicRequest.push({
                    type: actionTypes.CORRECTION_SAVE_SPECTRAL_DATA,
                    payload: {
                        result: payload.measurementResult,
                        tabId: newTabId
                    }
                });
            }

            if (state$.value.global.matchingAvailable) {
                epicRequest.push(
                    matchingActions.getMatchingColorfiles(newTabId, state$.value.formula[payload.tabId].product.id)
                );
            }

            return epicRequest;
        })
    );

const saveMeasureResult = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_SAVE_MEASURE_RESULT_AND_OPEN_PREVIEW),
        switchMap(({payload}) => {
            if (payload.tabId !== state$.value.navigation.activeTab) {
                return [];
            }

            return [
                navigationActions.openModalWithParams(payload.tabId, componentTypes.MATCHING_MEASUREMENT_MODAL, {
                    result: payload.result,
                    title: translate("matching.targetColor"),
                    subject: "",
                    searchButton: false,
                    searchCallback: null,
                    spectroCallback: actionTypes.MATCHING_SAVE_MEASURE_RESULT_AND_OPEN_PREVIEW
                })
            ];
        })
    );

const saveCorrectionMeasurementResult = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CORRECTION_SAVE_MEASUREMENT_RESULT),
        switchMap(({payload}) => {
            if (payload.tabId !== state$.value.navigation.activeTab) {
                return [];
            }

            return [
                navigationActions.openModalWithParams(payload.tabId, componentTypes.MATCHING_MEASUREMENT_MODAL, {
                    result: payload.result,
                    title: translate("matching.actualColor"),
                    subject: ": " + translate("general.measured"),
                    searchButton: true,
                    searchCallback: actionTypes.CORRECTION_SAVE_SPECTRAL_DATA,
                    spectroCallback: actionTypes.CORRECTION_SAVE_MEASUREMENT_RESULT
                })
            ];
        })
    );

const saveCorrectionTargetColorMeasurementResult = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.CORRECTION_SAVE_TARGET_COLOR_MEASUREMENT_RESULT),
        switchMap(({payload}) => {
            if (payload.tabId !== state$.value.navigation.activeTab) {
                return [];
            }

            return [
                navigationActions.openModalWithParams(payload.tabId, componentTypes.MATCHING_MEASUREMENT_MODAL, {
                    result: payload.result,
                    title: translate("matching.targetColor"),
                    subject: ": " + translate("general.measured"),
                    searchButton: true,
                    searchCallback: actionTypes.CORRECTION_SAVE_TARGET_COLOR_SPECTRAL_DATA,
                    spectroCallback: actionTypes.CORRECTION_SAVE_TARGET_COLOR_MEASUREMENT_RESULT
                })
            ];
        })
    );

const selectProduct = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_PRODUCT),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.tabId] === undefined ||
                state$.value.matchingTab[payload.tabId].activeProduct === null
            ) {
                return [];
            }

            return [
                matchingActions.getMatchingColorfiles(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].activeProduct.id
                )
            ];
        })
    );

const selectColorifle = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_COLORFILE),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.tabId] === undefined ||
                state$.value.matchingTab[payload.tabId].activeProduct === null ||
                state$.value.matchingTab[payload.tabId].activeColorfile === null
            ) {
                return [];
            }

            return [
                matchingActions.getColorfileDetail(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].activeProduct.id,
                    state$.value.matchingTab[payload.tabId].activeColorfile.id
                )
            ];
        })
    );

const selectBase = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_BASE, actionTypes.MATCHING_TAB_REMOVE_BASE),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.tabId] === undefined ||
                state$.value.matchingTab[payload.tabId].activeProduct === null ||
                state$.value.matchingTab[payload.tabId].activeBases.length === 0
            ) {
                return [];
            }

            return [
                matchingActions.getMatchingPackages(
                    payload.tabId,
                    state$.value.matchingTab[payload.tabId].activeProduct.id,
                    state$.value.matchingTab[payload.tabId].activeBases.map((base) => base.id)
                )
            ];
        })
    );

const selectMatchingResult = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SELECT_MATCHING_RESULT),
        switchMap(({payload}) => {
            if (
                state$.value.matchingTab[payload.tabId] === undefined ||
                state$.value.matchingTab[payload.tabId].activeProduct === null ||
                state$.value.matchingTab[payload.tabId].activeBases.length === 0
            ) {
                return [];
            }

            let customFormula = modelHelper.mapCustomFormulaToObject(payload.matchingResult.rgb);
            customFormula.colorantList = payload.matchingResult.colorantList;
            customFormula.formulaSourceId = formulaTypes.OWN_FORMULA_SOURCE.MATCHING;

            return [
                {
                    type: actionTypes.SAVE_CUSTOM_TAB_DATA,
                    payload: {
                        tabId: payload.tabId,
                        product: payload.matchingResult.bipp.product,
                        fandeck: modelHelper.mapCustomFandeckToObject(),
                        color: customFormula.color,
                        base: payload.matchingResult.bipp.base,
                        package: payload.matchingResult.bipp,
                        packageList: [payload.matchingResult.bipp],
                        margin: null,
                        formula: customFormula.updateColorantsByMl(
                            state$.value.settings.shotsCoef,
                            state$.value.settings.shotsName
                        ),
                        activeUnit: getterHelper.getValidActiveUnit(
                            state$.value.settings.lastUsedUnit,
                            state$.value.settings.visibleUnits,
                            componentTypes.CUSTOM_TAB,
                            state$.value.rcc.machine.typeName
                        ),
                        spectralData: state$.value.matchingTab[payload.tabId].measurementResult,
                        moreInfoText: null
                    }
                },
                {
                    type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                    payload: {
                        tabId: payload.tabId,
                        formula: customFormula.getColorantVolumes(),
                        numCanDone: 0,
                        numCanTotal: 1,
                        customCan: false
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_FORMULA_VALIDATION_ERRORS,
                        params: {
                            productId: payload.matchingResult.bipp.product.id,
                            baseId: payload.matchingResult.bipp.base.id,
                            formulaColorants: customFormula.getRealColorantAmounts(),
                            formulaCorrections: customFormula.isCorrection()
                                ? customFormula.getCorrectionAmounts()
                                : null,
                            packageId: payload.matchingResult.bipp.package.isCustom()
                                ? null
                                : payload.matchingResult.bipp.id,
                            customPackage: payload.matchingResult.bipp.package.isCustom(),
                            customPackageSize: payload.matchingResult.bipp.package.isCustom()
                                ? payload.matchingResult.bipp.package.nominalAmount
                                : null,
                            customPackageUnitId: payload.matchingResult.bipp.package.isCustom()
                                ? payload.matchingResult.bipp.package.unit
                                : null,
                            canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                state$.value.global.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.machine.allBatchNumbersList
                            )
                        }
                    },
                    method: handlers.GET_FORMULA_VALIDATION_ERRORS,
                    tabId: payload.tabId
                }
            ];
        })
    );

const setDraftFormulaScene = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MATCHING_TAB_SET_DRAFT_FORMULA_SCENE),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }
            if (
                state$.value.formula[payload.tabId].product === null ||
                state$.value.formula[payload.tabId].base === null ||
                state$.value.formula[payload.tabId].package === null
            ) {
                return [
                    {
                        type: actionTypes.MATCHING_TAB_INIT_DRAFT_FORMULA_SCENE,
                        payload: {
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.MATCHING_TAB_INIT_DRAFT_FORMULA_SCENE,
                    payload: {
                        tabId: payload.tabId
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productName: state$.value.formula[payload.tabId].product.name,
                            baseName: state$.value.formula[payload.tabId].base.name,
                            packageName: state$.value.formula[payload.tabId].package.package.name,
                            online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: payload.tabId
                }
            ];
        })
    );

const startQualityControl = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.QC_SAVE_MEASURE_RESULT_AND_RUN_COLOR_DIFFERENCE),
        switchMap(({payload}) => {
            if (state$.value.formula[payload.tabId] === undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_COMPARE_COLORS_QUALITY_CONTROL,
                        params: {
                            standardSpectralCurve: state$.value.formula[payload.tabId].spectralData.spectral,
                            sampleSpectralCurve: payload.result.spectral
                        }
                    },
                    method: handlers.GET_DIFFERENCE_DETAIL,
                    tabId: payload.tabId,
                    next: payload.next
                }
            ];
        })
    );

export default [
    matchingTabInit,
    matchingInit,
    matchingTabFromMenu,
    matchingTabInitSearch,
    matchingTabSelectFandeck,
    matchingTabSelectColor,
    matchingTabFromPalette,
    matchingTabFromStats,
    saveMeasureResult,
    saveCorrectionMeasurementResult,
    saveCorrectionTargetColorMeasurementResult,
    selectProduct,
    selectColorifle,
    selectBase,
    setMatchingProduct,
    selectMatchingResult,
    setDraftFormulaScene,
    startQualityControl
];
